import { ALGOLIA_PRICE_SORT_INDICES } from './client';

/**
 * Customer-tier pricing (PRO / FF / EMP) is applied on the storefront by
 * window.customerDiscount (global-fn.liquid), from per-product tags such as
 * "PRO40". Algolia's price replicas only know the regular selling price, so
 * for discounted customers the replica order has to be re-sorted on the client.
 */

export function getCustomerDiscountValue(tags) {
  if (typeof window !== 'undefined' && typeof window.customerDiscount === 'function') {
    return Number(window.customerDiscount(tags || [])) || 0;
  }

  return 0;
}

/**
 * The price a customer sees: the tier discount applies to compare-at when a
 * product is on sale, but never raises the price above the sale price.
 */
export function getDiscountedPrice(priceValue, compareAtPriceValue, customerDiscountValue) {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (!Number.isFinite(price)) {
    return 0;
  }

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return Math.min(compareAtPrice * (1 - customerDiscountValue), price);
  }

  return price * (1 - customerDiscountValue);
}

/**
 * @param {string} indexName - Algolia index or replica the results came from
 * @returns {'asc'|'desc'|null}
 */
export function getPriceSortDirection(indexName) {
  if (indexName === ALGOLIA_PRICE_SORT_INDICES.asc) return 'asc';
  if (indexName === ALGOLIA_PRICE_SORT_INDICES.desc) return 'desc';
  return null;
}

/**
 * Re-sorts price-sorted results by the price each card actually shows.
 * Returns the same array untouched when no price sort is active, when
 * `requireDiscount` is set and no item carries a customer discount, or when
 * the re-sorted order matches Algolia's, so callers can tell whether the order
 * changed by comparing references. The sort is stable: ties keep Algolia's order.
 *
 * @param {Object[]} items
 * @param {string} indexName - Algolia index or replica the items came from
 * @param {(item: Object) => {discount: number, price: number}} getCustomerPrice -
 *   the item's customer discount and the price its card shows with that discount
 * @param {Object} [options]
 * @param {boolean} [options.requireDiscount=true] - only re-sort when some item is discounted
 * @returns {Object[]}
 */
export function sortByCustomerPrice(items, indexName, getCustomerPrice, { requireDiscount = true } = {}) {
  const direction = getPriceSortDirection(indexName);
  if (!direction || !items.length) return items;

  let hasDiscount = false;
  const decorated = items.map((item, index) => {
    const { discount, price } = getCustomerPrice(item);
    if (discount > 0) hasDiscount = true;
    return { item, index, price };
  });

  if (requireDiscount && !hasDiscount) return items;

  const sign = direction === 'asc' ? 1 : -1;
  decorated.sort((a, b) => (a.price - b.price) * sign || a.index - b.index);

  if (decorated.every(({ index }, position) => index === position)) return items;
  return decorated.map(({ item }) => item);
}
